import { billingPeriodLabelToSlug } from './plan-wc-product.util';

/**
 * Term end for a subscription preview (SUSEE-1570).
 *
 * Auto-renew on follows calendar periods (Razorpay charge_at preview).
 * Auto-renew off uses calendar months for monthly and yearly, and fixed
 * day counts for quarterly (90) and semi-annual (180).
 *
 * Month-end: the day is kept when the target month has it, otherwise the
 * date lands on the last day of that month (30 Nov + 3 months = 28 Feb,
 * not 2 Mar). Razorpay's own month-end rule is not confirmed; change
 * addCalendarMonths if a live subscription disagrees.
 */
export function getTermEndDate(
  startDate: string | Date | null | undefined,
  billingPeriod: string | null | undefined,
  autoRenew: boolean
): Date | null {
  const start = parseLocalCalendarDate(startDate);
  if (!start) {
    return null;
  }
  const period = billingPeriodLabelToSlug(billingPeriod);
  if (period === 'yearly') {
    return addCalendarMonths(start, 12);
  }
  if (period === 'quarterly') {
    return autoRenew ? addCalendarMonths(start, 3) : addCalendarDays(start, 90);
  }
  if (period === 'semiannual') {
    return autoRenew ? addCalendarMonths(start, 6) : addCalendarDays(start, 180);
  }
  return addCalendarMonths(start, 1);
}

export interface BillingHeaderDateInput {
  autoRenew: boolean;
  /** Razorpay next charge (`rzp_next_renewal` / `charge_at`). */
  rzpNextRenewalDate?: string | Date | null;
  /** Backend `expires_at` (Razorpay `current_end` when auto-renew is on). */
  expiresAt?: string | Date | null;
}

export interface BillingHeaderDate {
  label: 'Renews' | 'Valid until';
  date: Date | null;
}

/**
 * Billing hero date. Auto-renew on uses the Razorpay next charge, and falls
 * back to expires_at only when that value is missing. Auto-renew off stays
 * on expires_at.
 */
export function resolveBillingHeaderDate(input: BillingHeaderDateInput): BillingHeaderDate {
  if (input.autoRenew) {
    const renewal = parseLocalCalendarDate(input.rzpNextRenewalDate);
    if (renewal) {
      return { label: 'Renews', date: renewal };
    }
    return { label: 'Renews', date: parseLocalCalendarDate(input.expiresAt) };
  }
  return { label: 'Valid until', date: parseLocalCalendarDate(input.expiresAt) };
}

/** Whole calendar days from `from` to `to`. Negative when `to` is earlier. */
export function calendarDaysBetween(
  from: string | Date | null | undefined,
  to: string | Date | null | undefined
): number | null {
  const start = parseLocalCalendarDate(from);
  const end = parseLocalCalendarDate(to);
  if (!start || !end) {
    return null;
  }
  return Math.round((end.getTime() - start.getTime()) / 86400000);
}

/**
 * Calendar date in the local timezone.
 * `new Date('2027-02-04')` is UTC midnight and can display as the previous day.
 * Date-only strings, and datetimes with no zone, use the calendar numbers as-is.
 */
export function parseLocalCalendarDate(
  value: string | Date | null | undefined
): Date | null {
  if (value == null || value === '') {
    return null;
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const text = String(value).trim();
  if (!text) {
    return null;
  }
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (dateOnly) {
    return localDate(Number(dateOnly[1]), Number(dateOnly[2]), Number(dateOnly[3]));
  }
  const hasZone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(text);
  const localDateTime = /^(\d{4})-(\d{2})-(\d{2})[T ]/.exec(text);
  if (localDateTime && !hasZone) {
    return localDate(
      Number(localDateTime[1]),
      Number(localDateTime[2]),
      Number(localDateTime[3])
    );
  }
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function localDate(year: number, month: number, day: number): Date | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

function addCalendarMonths(date: Date, months: number): Date {
  const day = date.getDate();
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(day, lastDay));
  return target;
}

function addCalendarDays(date: Date, days: number): Date {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  next.setDate(next.getDate() + days);
  return next;
}
